import React from 'react';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import GridType from '@/src/components/Modules-revamp/GridType';
import Animation from 'src/components/Modules-revamp/Animation';
import ModuleWidth from '@/src/components/Modules-revamp/ModuleWidthSelect';
import ItemInARow from '@/src/components/Modules-revamp/ItemInARow';
import LeaveGapBetweenProducts from '@/src/components/Modules-revamp/LeaveGapBetween';
import OverflowItemsDisplayOption from '@/src/components/Modules-revamp/OverflowItemsDisplayOption';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
} from '@/src/strategies/util';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import { get } from 'lodash';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import ContentText from '@/src/components/Modules-revamp/ContentText';
import {
  checkResponsiveContentGrid,
  validateContentImgList,
} from '@/src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import ContentPages from '@/src/components/Modules-revamp/ContentPages';
import moduleStyleRender from '../../ModuleStyle';
import contentGridRenderTemplate from 'src/strategies/render/contentGrid';
import { Field } from '@/src/components/RcForm';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { useContentPageGrid } from '../../hooks/useContentPageGrid';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
};

const contentGridRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const {
    handleOnSectionReset,
    handleOnSectionCopy,
    // options,
    salesChannelMode,
  } = extra;

  const list = contentGridRenderTemplate(values, breakpoint, salesChannelMode);
  // const { inUsePages = [] } = options || {};

  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => contentGridComponent(f, breakpoint, extra));
    }
  };

  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        const validator: any =
          get(item, 'validator') ||
          function () {
            return void 0;
          };

        const validatorWithExtraProps = (value, extra) => {
          const res = validator(value, {
            ...(extra || {}),
            //    contentPages: inUsePages,
          });

          return res;
        };

        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={validatorWithExtraProps}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const contentGridComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const {
    animations,
    hoverAnimations,
    form,
    //  options,
    onModalConfirm,
    onAdd,
    onDelete,
    onSort,
    scContentPages,
  } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.GridType: {
      return (
        <GridType disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.ContentPageToShow: {
      return (
        <ItemInARow
          moduleType={EnumModuleType.ResponsiveContentGrid}
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetweenProducts
          moduleType={EnumModuleType.ResponsiveContentGrid}
          options={data?.options || []}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.OverflowItems: {
      return (
        <OverflowItemsDisplayOption
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.ContentText: {
      return (
        <ContentText
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.Contents: {
      const { checkEachContentPageError } = checkResponsiveContentGrid();
      const {
        checkContentPageExistingError,
        checkContentPageImageError,
        checkContentPageSummaryError,
        checkSubtitleError,
      } = validateContentImgList();
      return (
        <ContentPages
          validators={{
            contentPage: checkContentPageExistingError,
            image: checkContentPageImageError,
            subtitle: checkSubtitleError,
            summary: checkContentPageSummaryError,
          }}
          allValidator={checkEachContentPageError}
          moduleType={EnumModuleType.ResponsiveContentCarousel}
          draggableId={draggableId}
          breakpoint={breakpoint}
          disabled={data?.disabled}
          //  contentOptions={options}
          hasModal={data?.hasModal}
          onModalConfirm={onModalConfirm}
          onAdd={onAdd}
          onDelete={onDelete}
          onSort={onSort}
          scContentPages={scContentPages}
        />
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }: any) => {
  const { handleOnModalConfirm, handleOnAdd, handleOnDelete, handleOnSort } =
    useContentPageGrid({ form });
  const {
    animations,
    hoverAnimations,
    buttons, //options,
    scContentPages,
  } = React.useContext(ContentModuleGroupPreloadingContext);
  return (
    <Field shouldUpdate>
      {() =>
        contentGridRenderer(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          animations,
          buttons,
          hoverAnimations,
          // options,
          form,
          scContentPages,
          ...extra,
        })
      }
    </Field>
  );
};
